import type { AddressType, PartyType, TaxIdType } from '../generated/enums.js';

/**
 * WorkOS org API key scopes for the External API customer surface.
 *
 * Customers are upserted by `externalReferenceId` or tax id (see
 * `ExternalCustomerIngestionService`). There is no dedicated
 * `externalReferenceId` column on `ArCustomer` — the integrator's reference
 * is stored on the existing `ArCustomer.code` field, exactly like
 * `ArCustomerService.importCanonical()`'s `externalCustomerId` and
 * `ExternalCreateInvoiceRequest.customerCode` already do. No schema change.
 */
export const ExternalCustomerScope = {
  READ: 'external:customers:read',
  WRITE: 'external:customers:write',
} as const;
export type ExternalCustomerScope =
  (typeof ExternalCustomerScope)[keyof typeof ExternalCustomerScope];

/** All External API customer scopes (WorkOS org API key allowlist + route guards). */
export const EXTERNAL_API_CUSTOMER_SCOPES = [
  ExternalCustomerScope.READ,
  ExternalCustomerScope.WRITE,
] as const satisfies readonly ExternalCustomerScope[];

export interface ExternalCustomerAddress {
  addressType?: AddressType;
  line1: string;
  number?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  countryCode?: string;
}

/**
 * Most customers ingested through this route are CPF individuals — there is
 * no CNPJ enrichment (SERPRO lookup) path here, so the full payload is
 * required rather than resolved from a tax-id lookup.
 */
export interface ExternalCreateCustomerRequest {
  entityId: string;
  externalReferenceId: string;
  partyType: PartyType;
  legalName: string;
  /**
   * Optional (ALC-5184): a customer with no document is identified by `externalReferenceId`.
   * Sent together; a CPF/CNPJ sent for a customer in Brazil must be valid
   * (400 `INVALID_CUSTOMER_TAX_ID`).
   */
  taxIdType?: TaxIdType;
  taxIdValue?: string;
  email: string;
  phoneNumber: string;
  address: ExternalCustomerAddress;
}

export type ExternalUpdateCustomerRequest = Partial<
  Omit<ExternalCreateCustomerRequest, 'entityId' | 'externalReferenceId'>
>;

export interface ExternalCustomerResponse {
  id: string;
  entityId: string;
  externalReferenceId: string;
  partyType: PartyType;
  legalName: string;
  taxIdType?: TaxIdType | null;
  taxIdValue?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  address?: ExternalCustomerAddress | null;
  createdAt: string;
  updatedAt?: string | null;
}
